<template>
  <v-dialog
    :model-value="props.modelValue"
    @update:model-value="handleOverlayClick"
    :width="dialogWidth"
    :max-width="props.width"
    :fullscreen="props.fullscreen"
    scrim="false"
  >
    <div class="dialog-wrapper">
      <v-btn
        v-if="props.showArrows"
        class="nav-arrow"
        icon="mdi-chevron-left"
        variant="text"
        size="large"
        @click="emit('leftClicked')"
      ></v-btn>

      <v-card class="dialog-card d-flex flex-column flex-grow-1"
        :class="{ 'dialog-card--fullscreen': props.fullscreen }">
        <v-card-title v-if="!props.hideHeader" class="d-flex align-center bg-primary text-white">
          <span class="text-h5">{{ props.title }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="emit('closeDialog')"
          ></v-btn>
        </v-card-title>

        <RcDivider v-if="!props.hideHeader" class="ma-0"/>

        <v-card-text class="dialog-content overflow-y-auto" :class="props.contentClass">
          <slot></slot>
        </v-card-text>

        <RcDivider v-if="!props.hideFooter" class="ma-0"/>

        <v-card-actions v-if="!props.hideFooter">
          <v-spacer></v-spacer>
          <slot name="actions"></slot>
        </v-card-actions>
      </v-card>

      <v-btn
        v-if="props.showArrows"
        class="nav-arrow"
        icon="mdi-chevron-right"
        variant="text"
        size="large"
        @click="emit('rightClicked')"
      ></v-btn>
    </div>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue';
import RcDivider from '@/v3/components/library/divider/RcDivider.vue';
import DialogUtils from '@/v3/utils/DialogUtils';
const props = defineProps({
  modelValue: { type: Boolean, required: true },
  title: { type: String, default: 'Dialog' },
  width: { type: [String, Number], default: DialogUtils.MEDIUM },
  showArrows: { type: Boolean, default: false },
  hideFooter: { type: Boolean, default: false },
  hideHeader: { type: Boolean, default: false },
  fullscreen: { type: Boolean, default: false },
  contentClass: { type: String, default: '' },
  fillWidth: { type: Boolean, default: false },
});

/*
 * `width` alone is a cap: a dialog is shrink-to-fit, so it claims only as much
 * as its content asks for. A form whose columns declare a wrap threshold rather
 * than a preferred width asks for less than it should, and the dialog comes up
 * narrower than its cap with the fields squeezed for no reason. `fillWidth`
 * makes such a dialog take the width it was given. Fullscreen sets its own
 * width in the stylesheet, and an inline width would beat it.
 */
const dialogWidth = computed(() => {
  return props.fillWidth && !props.fullscreen ? props.width : undefined;
});

const emit = defineEmits(['update:modelValue', 'closeDialog', 'leftClicked', 'rightClicked']);

const handleOverlayClick = (value) => {
  if (value === false) {
    emit('closeDialog');
  }
};
</script>

<style scoped>
/*
 * The content area takes whatever the header and actions leave over, rather
 * than a fixed 75vh. A vh cap is measured in CSS pixels, so browser zoom shrank
 * the scroll region at the same time as it grew the text inside it -- the user
 * asked for larger type and got less of the form per screen. Letting the flex
 * child grow means the dialog always uses the height it actually has, and a
 * fullscreen dialog stops reserving a quarter of the screen it cannot use.
 */
.dialog-card {
  max-height: 92vh;
}

.dialog-card--fullscreen {
  max-height: 100%;
}

.dialog-content {
  /* Vuetify already gives .v-card-text flex: 1 1 auto, so it grows on its own.
     What it lacks is this: without min-height a flex child refuses to shrink
     below its content, and the overflow lands on the dialog instead of here. */
  min-height: 0;
}

.dialog-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.nav-arrow {
  color: white;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: 0.6;
  transition: opacity 0.2s ease-in-out;
  z-index: 1;
}

.nav-arrow:hover {
  opacity: 1;
}
</style>
